import imageCompression from 'browser-image-compression';
import i18n from 'i18next';

import { errorToastMessage } from '../../components/layout/toast-root/toast';
import { INewFile } from '../../types/interfaces/new-file';
import { checkIsImage } from './file-utils';

const fileMaxSize = 10485760; // 10mb

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader error'));
    reader.onabort = () => reject(new Error('FileReader abort'));
    reader.readAsDataURL(file);
  });
}

function compressFile(file: File) {
  if (checkIsImage(file.type)) {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    const previewOptions = {
      maxWidthOrHeight: 150,
      useWebWorker: true,
    };

    return Promise.all([
      imageCompression(file, options),
      imageCompression(file, previewOptions),
    ]);
  }

  return Promise.all([Promise.resolve(file), Promise.resolve(null)]);
}

async function handleFile(
  file: File,
  previewFile: File | null,
  uploadPreview: boolean,
): Promise<INewFile | undefined> {
  if (file?.size > fileMaxSize) {
    const errorMessage = i18n.t('messages.fileOversized10mb', {
      1: file.name,
    });
    errorToastMessage(errorMessage);
    return undefined;
  }

  try {
    const fileBlob = await readFileAsDataUrl(file);
    let previewFileBlob: string | null = null;

    if (previewFile) {
      previewFileBlob = await readFileAsDataUrl(previewFile);
    }

    const fileId = Math.floor(Math.random() * 1000000);

    const result: INewFile = {
      id: fileId,
      name: file.name,
      mimeType: file.type,
      size: file.size,
      createdAt: new Date(),
      previewFileBlob,
      fileBlob,
    };

    if (uploadPreview && !previewFileBlob) {
      errorToastMessage(i18n.t('messages.fileReadFailed'));
      return undefined;
    }

    return result;
  } catch (error) {
    console.error('Error reading or preparing file for upload:', error);
    errorToastMessage(i18n.t('messages.fileReadFailed'));
    return undefined;
  }
}

export function uploadFilesUtils(
  files: FileList | null,
  uploadPreview = false,
) {
  if (!files?.length) {
    return Promise.resolve([]);
  }

  return Promise.all(
    [...files].map(async (file) => {
      try {
        if (!uploadPreview) {
          return await handleFile(file, null, uploadPreview);
        }

        const [compressedFile, previewFile] = await compressFile(file);
        return await handleFile(compressedFile, previewFile, uploadPreview);
      } catch (error) {
        console.error('Error processing file for upload:', error);
        errorToastMessage(i18n.t('messages.fileReadFailed'));
        return undefined;
      }
    }),
  );
}
